import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useCreatePromptMutation } from '../../../store/prompts/promptsApi';
import { usePrompts } from './PromptsContext';
import {
    ICreatePromptPayload,
    MODEL_OPTIONS,
    PROVIDER_OPTIONS,
    getPromptId,
} from './types';

const CreatePromptPage = () => {
    const navigate = useNavigate();
    const { canEdit } = usePrompts();
    const [createPrompt, { isLoading, error: apiError }] = useCreatePromptMutation();
    const [submitError, setSubmitError] = useState<string | null>(null);

    const { register, handleSubmit, control, formState: { errors } } = useForm<ICreatePromptPayload>({
        defaultValues: {
            name: '',
            key: '',
            description: '',
            allowedProviders: ['openai'],
            allowedModels: ['gpt-4o'],
            systemPrompt: '',
            prompt: '',
            configuration: {
                temperature: 0.2,
                maxTokens: 4000,
                topP: 1,
            },
        },
    });

    if (!canEdit) {
        return (
            <div>
                <h1 className='text-xl md:text-2xl font-bold text-[#101010] dark:text-primary-darkMode'>
                    Create prompt
                </h1>
                <p className='mt-4 text-sm text-red-600 dark:text-red-400'>
                    You do not have permission to create prompts.
                </p>
                <button
                    type='button'
                    onClick={() => navigate('/prompts')}
                    className='mt-4 px-4 py-2 rounded-md border border-gray-300 dark:border-gray-700 text-sm'
                >
                    Back to list
                </button>
            </div>
        );
    }

    const getApiErrorMessage = () => {
        if (!apiError) return null;
        if (typeof apiError === 'object' && apiError !== null && 'data' in apiError) {
            const data = (apiError as { data?: { message?: string } }).data;
            return data?.message || 'Failed to create prompt.';
        }
        if (typeof apiError === 'object' && apiError !== null && 'message' in apiError) {
            return String((apiError as { message?: string }).message) || 'Failed to create prompt.';
        }
        return 'Failed to create prompt.';
    };

    const onSubmit = async (values: ICreatePromptPayload) => {
        setSubmitError(null);
        try {
            const created = await createPrompt(values).unwrap();
            navigate(`/prompts/${getPromptId(created)}`);
        } catch (err: unknown) {
            const message =
                (err as { data?: { message?: string } })?.data?.message ||
                (err as { error?: string })?.error ||
                'Failed to create prompt.';
            setSubmitError(message);
        }
    };

    return (
        <div>
            <button
                type='button'
                onClick={() => navigate('/prompts')}
                className='text-sm text-primary dark:text-primary-darkMode hover:underline mb-3'
            >
                ← Back to prompts
            </button>
            <h1 className='text-xl md:text-2xl font-bold text-[#101010] dark:text-primary-darkMode'>
                Create prompt
            </h1>
            <p className='mt-2 text-sm text-gray-600 dark:text-gray-400'>
                Create a new prompt with providers, models, and configuration.
            </p>

            <form
                onSubmit={handleSubmit(onSubmit)}
                className='mt-6 max-w-3xl border border-gray-200 dark:border-borderColor-darkMode rounded-xl p-5'
            >
                <div className='space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                        <label className='block text-sm font-medium mb-1 dark:text-white'>Name</label>
                        <input
                            {...register('name', { required: 'Name is required' })}
                            className='w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black px-3 py-2 text-sm dark:text-white'
                        />
                        {errors.name && <p className='text-xs text-red-600 mt-1'>{errors.name.message}</p>}
                    </div>
                    <div>
                        <label className='block text-sm font-medium mb-1 dark:text-white'>Key</label>
                        <input
                            {...register('key', {
                                required: 'Key is required',
                                pattern: {
                                    value: /^[a-z0-9-]+$/,
                                    message: 'Use lowercase letters, numbers, and hyphens only',
                                },
                            })}
                            placeholder='hotel-comparison'
                            className='w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black px-3 py-2 text-sm dark:text-white font-mono'
                        />
                        {errors.key && <p className='text-xs text-red-600 mt-1'>{errors.key.message}</p>}
                    </div>
                </div>

                <div>
                    <label className='block text-sm font-medium mb-1 dark:text-white'>Description</label>
                    <input
                        {...register('description', { required: 'Description is required' })}
                        className='w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black px-3 py-2 text-sm dark:text-white'
                    />
                    {errors.description && (
                        <p className='text-xs text-red-600 mt-1'>{errors.description.message}</p>
                    )}
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                        <label className='block text-sm font-medium mb-2 dark:text-white'>Allowed providers</label>
                        <Controller
                            name='allowedProviders'
                            control={control}
                            rules={{ validate: (v) => v.length > 0 || 'Select at least one provider' }}
                            render={({ field }) => (
                                <div className='space-y-2'>
                                    {PROVIDER_OPTIONS.map((provider) => (
                                        <label key={provider} className='flex items-center gap-2 text-sm dark:text-white'>
                                            <input
                                                type='checkbox'
                                                checked={field.value.includes(provider)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        field.onChange([...field.value, provider]);
                                                    } else {
                                                        field.onChange(field.value.filter((p) => p !== provider));
                                                    }
                                                }}
                                                className='h-4 w-4'
                                            />
                                            {provider}
                                        </label>
                                    ))}
                                </div>
                            )}
                        />
                        {errors.allowedProviders && (
                            <p className='text-xs text-red-600 mt-1'>{errors.allowedProviders.message}</p>
                        )}
                    </div>
                    <div>
                        <label className='block text-sm font-medium mb-2 dark:text-white'>Allowed models</label>
                        <Controller
                            name='allowedModels'
                            control={control}
                            rules={{ validate: (v) => v.length > 0 || 'Select at least one model' }}
                            render={({ field }) => (
                                <div className='space-y-2 max-h-40 overflow-y-auto'>
                                    {MODEL_OPTIONS.map((model) => (
                                        <label key={model} className='flex items-center gap-2 text-sm dark:text-white'>
                                            <input
                                                type='checkbox'
                                                checked={field.value.includes(model)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        field.onChange([...field.value, model]);
                                                    } else {
                                                        field.onChange(field.value.filter((m) => m !== model));
                                                    }
                                                }}
                                                className='h-4 w-4'
                                            />
                                            {model}
                                        </label>
                                    ))}
                                </div>
                            )}
                        />
                        {errors.allowedModels && (
                            <p className='text-xs text-red-600 mt-1'>{errors.allowedModels.message}</p>
                        )}
                    </div>
                </div>

                <div>
                    <label className='block text-sm font-medium mb-1 dark:text-white'>System prompt</label>
                    <textarea
                        rows={4}
                        {...register('systemPrompt', { required: 'System prompt is required' })}
                        className='w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black px-3 py-2 text-sm dark:text-white font-mono'
                    />
                    {errors.systemPrompt && (
                        <p className='text-xs text-red-600 mt-1'>{errors.systemPrompt.message}</p>
                    )}
                </div>

                <div>
                    <label className='block text-sm font-medium mb-1 dark:text-white'>Prompt</label>
                    <textarea
                        rows={6}
                        {...register('prompt', { required: 'Prompt is required' })}
                        className='w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black px-3 py-2 text-sm dark:text-white font-mono'
                    />
                    {errors.prompt && (
                        <p className='text-xs text-red-600 mt-1'>{errors.prompt.message}</p>
                    )}
                </div>

                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                    <div>
                        <label className='block text-sm font-medium mb-1 dark:text-white'>Temperature</label>
                        <input
                            type='number'
                            step='0.1'
                            min='0'
                            max='2'
                            {...register('configuration.temperature', { valueAsNumber: true, required: true })}
                            className='w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black px-3 py-2 text-sm dark:text-white'
                        />
                    </div>
                    <div>
                        <label className='block text-sm font-medium mb-1 dark:text-white'>Max tokens</label>
                        <input
                            type='number'
                            min='1'
                            {...register('configuration.maxTokens', { valueAsNumber: true, required: true })}
                            className='w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black px-3 py-2 text-sm dark:text-white'
                        />
                    </div>
                    <div>
                        <label className='block text-sm font-medium mb-1 dark:text-white'>Top P</label>
                        <input
                            type='number'
                            step='0.1'
                            min='0'
                            max='1'
                            {...register('configuration.topP', { valueAsNumber: true, required: true })}
                            className='w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black px-3 py-2 text-sm dark:text-white'
                        />
                    </div>
                </div>

                {(submitError || getApiErrorMessage()) && (
                    <p className='text-sm text-red-600 dark:text-red-400'>
                        {submitError || getApiErrorMessage()}
                    </p>
                )}

                <div className='flex justify-end gap-3 pt-2'>
                    <button
                        type='button'
                        onClick={() => navigate('/prompts')}
                        className='px-4 py-2 rounded-md border border-gray-300 dark:border-gray-700 text-sm'
                    >
                        Cancel
                    </button>
                    <button
                        type='submit'
                        disabled={isLoading}
                        className='px-4 py-2 rounded-md text-sm text-white bg-primary-default disabled:opacity-60'
                    >
                        {isLoading ? 'Creating...' : 'Create prompt'}
                    </button>
                </div>
                </div>
            </form>
        </div>
    );
};

export default CreatePromptPage;
